import type { ClassTime } from '../render/models/runtime.js';
import type { LiveState } from './session-client.js';

/**
 * The class's shared time as this headset hears it (ADR-054, amended 20 Oct 2026; docs/05 "The class
 * clock"): `session.state`'s `clock` says the class time when the message left the API and the class's
 * run of steps. A model going round keeps its lap to it, so every headset in a class shows the model at
 * the same point of its ring, whenever it started the step, loaded, joined or teleported.
 *
 * The offset between this headset's clock and the class's is the largest of the last few
 * (class time sent - local time heard): each sample is the true offset less how long its message took,
 * so the largest is the one that came quickest. Paused, the class time stands at what it was told.
 */

/** What `session.state` carries as `clock`. */
export type ClassClockWire = {
  readonly now: number;
  readonly run: readonly { readonly stepId: string; readonly at: number }[];
};

/** How many samples it keeps: old ones age out, so clocks that drift apart are followed. */
const SAMPLES = 8;
/** A sample this much (ms) below the best: the class time was set back (an API restart). */
const SET_BACK = 1000;

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** A state message's clock, checked; undefined from an older API, or one that is not a clock. */
export function classClockOf(value: unknown): ClassClockWire | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { now, run } = value as { now?: unknown; run?: unknown };
  if (!finite(now) || !Array.isArray(run) || run.length === 0) return undefined;
  const steps: { stepId: string; at: number }[] = [];
  for (const step of run as unknown[]) {
    if (typeof step !== 'object' || step === null) return undefined;
    const { stepId, at } = step as { stepId?: unknown; at?: unknown };
    if (typeof stepId !== 'string' || !finite(at)) return undefined;
    steps.push({ stepId, at });
  }
  return { now, run: steps };
}

export class ClassClock {
  /** Class ms − local ms, from the last state messages while the class ran. */
  #samples: number[] = [];
  /** The class time while it stands (paused, or not started). */
  #standing: number | undefined;
  #run: ClassClockWire['run'] = [];
  #heard = false;
  #free = false;

  /** A new lesson: nothing heard yet. */
  reset(): void {
    this.#samples = [];
    this.#standing = undefined;
    this.#run = [];
    this.#heard = false;
    this.#free = false;
  }

  /** A state message, heard at `localMs` (this headset's monotonic clock). */
  hear(state: LiveState, localMs: number): void {
    const wire = classClockOf(state.clock);
    if (!wire) {
      // An older API: no class time, every headset on its own.
      this.reset();
      return;
    }
    this.#heard = true;
    this.#run = wire.run;
    this.#free = state.control === 'free';
    if (state.paused === true || state.phase === 'preparing') {
      this.#standing = wire.now;
      // Its offset changes by the time it stood: measured afresh once it runs.
      this.#samples = [];
      return;
    }
    this.#standing = undefined;
    const sample = wire.now - localMs;
    if (this.#samples.length > 0 && sample < Math.max(...this.#samples) - SET_BACK) {
      this.#samples = [];
    }
    this.#samples.push(sample);
    if (this.#samples.length > SAMPLES) this.#samples.shift();
  }

  /** The class time now, ms; undefined before any is heard. */
  nowMs(localMs: number): number | undefined {
    if (this.#standing !== undefined) return this.#standing;
    if (this.#samples.length === 0) return undefined;
    return localMs + Math.max(...this.#samples);
  }

  /**
   * The class time for a lesson's models (seconds, steps by index); undefined outside a class's
   * shared time: nothing heard, an older API, a free class (each pupil on their own step), or a run
   * naming a step this lesson does not have.
   */
  time(localMs: number, steps: readonly { readonly id: string }[]): ClassTime | undefined {
    if (!this.#heard || this.#free) return undefined;
    const now = this.nowMs(localMs);
    if (now === undefined) return undefined;
    const run: { index: number; at: number }[] = [];
    for (const step of this.#run) {
      const index = steps.findIndex((candidate) => candidate.id === step.stepId);
      if (index < 0) return undefined;
      run.push({ index, at: step.at / 1000 });
    }
    return { now: now / 1000, run };
  }
}
